<template>
  <article class="panel">
    <NrTabs class="rs-tabs" v-model="activeTabId"
      ><NrTab title="Timeline" id="timeline-tab">
        <TimelineTab :noTimeline="!timeline.length">
          <template #timelineFilter>
            <NrSelect
              class="nr-select-timeline"
              v-if="$can('read', 'staff_only_items')"
              v-model="timelineFilters"
              :options="timelineFilterList"
              placeholder="Select an event to filter timeline"
              multiple
            >
              <template v-slot:label>
                <span>Filter by timeline event</span>
              </template>
            </NrSelect>
          </template>
          <template #cards>
            <TimelineCard
              v-for="item in timeline"
              :key="item.timeline_id"
              :canPin="canPin(item)"
              :pinned="item.timeline_is_pinned"
              :collapsable="
                canCollapse(
                  item.timeline_is_collapsable,
                  item.timeline_display_text
                )
              "
              :collapsed="false"
              :id="item.timeline_id"
              :with-actions="timelineActions(item)"
              :editIcon="editIcon(item)"
              :deleteIcon="false"
              :changeVisibilityIcon="changeVisibilityIcon(item)"
              :color="item.color"
              :icon-name="item.iconname"
              :header="timelineCardHeader(item)"
              :subheader="item.timeline_subheader"
              :header-right="item.timeline_state"
              :footer-left="item.timeline_byline"
              :emphasize-header="
                item.timeline_state === 'Anyone with access to this request'
              "
              @on-visibility="handleChangeVisibility(item)"
              @on-edit="handleEditMessageText(item)"
              @on-delete="() => {}"
              @on-collapse="
                handleCollapse(
                  item.timeline_id,
                  canCollapse(
                    item.timeline_is_collapsable,
                    item.timeline_display_text
                  )
                )
              "
              @on-pin-click="handlePinChange(item)"
            >
              <p
                class="wrap-text"
                v-html="
                  timelineItemDisplayText(
                    item.timeline_id,
                    item.timeline_display_text
                  )
                "
              />
              <div
                class="invoice-timeline"
                v-if="
                  !this.isCollapsed(item.timeline_id) &&
                  item.event_type_name === 'Invoice Sent' &&
                  account.payments
                "
              >
                <InvoiceTimelineEvent
                  :lineItems="item.timeline_invoice_line_items"
                  :surcharges="item.timeline_invoice_surcharges"
                  :staffHours="item.timeline_invoice_staff_hours"
                  :totalAmount="item.timeline_invoice_total_amount"
                />
                <div class="invoice-links">
                  <template
                    v-if="
                      account.payments &&
                      (isCurrentRequester || $can('read', 'staff_only_items'))
                    "
                  >
                    <NrTooltip
                      label="A PDF is not available for this Invoice."
                      position="left"
                      v-if="!item.timeline_invoice_has_pdf"
                    >
                      <NrButton
                        class="no-wrap"
                        variant="minimal-primary"
                        disabled="true"
                        aria-label="Download PDF"
                      >
                        <NrIcon
                          class="inline-icon"
                          name="download"
                          aria-hidden="true"
                        />Download PDF
                      </NrButton>
                    </NrTooltip>
                    <NrButton
                      v-else
                      class="no-wrap"
                      variant="minimal-primary"
                      aria-label="Download PDF"
                      @click="downloadInvoicePDF(item.timeline_invoice_id)"
                    >
                      <NrIcon
                        class="inline-icon"
                        name="download"
                        aria-hidden="true"
                      />Download PDF
                    </NrButton>
                  </template>
                  &nbsp;
                  <NrButton
                    v-if="
                      isCurrentRequester &&
                      item.timeline_invoice_outstanding &&
                      account.payments &&
                      account.payment_processor_activated
                    "
                    class="no-wrap payment-notice-button"
                    variant="bordered-primary"
                    aria-label="Pay invoice"
                    :disabled="!!invoiceProcessing"
                    @click="setActiveModal('requestPaymentsDialog')"
                  >
                    <NrIcon class="inline-icon" name="dollar-sign" /> Pay
                    invoice
                  </NrButton>
                </div>
              </div>
            </TimelineCard>
            <NrModal
              title="Replacing a pinned note"
              :show="currentAction === 'replacingPinnedNote'"
              primaryButtonText="Continue"
              hasSecondaryButton
              @on-secondary-click="setCurrentAction('')"
              @on-primary-click="updateNotePin"
            >
              <slot
                >You are about to replace a pinned note with another. Only one
                note can be pinned at a time.
              </slot>
            </NrModal>
            <change-message-visibility-modal
              :selected-message="selectedTimelineEvent"
              :active="editMessageVisibility"
              @on-close-message-visibility-modal="
                closeChangeMessageVisibilityModal
              "
            ></change-message-visibility-modal>
            <change-message-text-modal
              :selected-message="selectedTimelineEvent"
              v-if="editMessageText"
              :active="editMessageText"
              @on-close-message-visibility-modal="
                closeChangeMessageVisibilityModal
              "
            ></change-message-text-modal>
            <NrButton
              v-if="canShowMoreTimeline"
              @click="handleTimelineShowMore"
              variant="minimal-neutral"
              class="show-more"
            >
              Show me more
            </NrButton>
          </template>
        </TimelineTab> </NrTab
      ><NrTab title="Documents" id="documents-tab">
        <DocumentsTab
          :open="activeTabId === 'documents-tab'"
          :docs="documents"
          :folders="documentsFolders"
        /> </NrTab
      ><NrTab
        v-if="$can('read', 'staff_only_items')"
        title="Tasks"
        id="tasks-tab"
        ><TasksTab
          :tasks="tasks"
          :timelineFilters="timelineFilters"
          :currentTimelinePageNum="currentTimelinePageNum"
        />
      </NrTab>
    </NrTabs>
  </article>
</template>

<script>
import TimelineTab from './TimelineTab'
import ChangeMessageTextModal from './ChangeMessageTextModal'
import ChangeMessageVisibilityModal from './ChangeMessageVisibilityModal'
import DocumentsTab from './DocumentsTab'
import TasksTab from './TasksTab.vue'
import TimelineCard from './TimelineCard.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrTabs, { NrTab } from '@nextrequestco/components/src/components/NrTabs'
import NrButton from '@nextrequestco/components/src/components/NrButton'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import InvoiceTimelineEvent from './InvoiceTimelineEvent.vue'
import { mapActions, mapState, mapMutations } from 'vuex'

export default {
  name: 'RequestTabs',
  components: {
    ChangeMessageTextModal,
    ChangeMessageVisibilityModal,
    TasksTab,
    TimelineTab,
    DocumentsTab,
    TimelineCard,
    NrSelect,
    NrTabs,
    NrTab,
    NrButton,
    NrTooltip,
    NrModal,
    InvoiceTimelineEvent,
    NrIcon
  },
  props: {
    timeline: {
      type: Array
    },
    documents: {
      type: Array
    },
    documentsFolders: {
      type: Array
    },
    tasks: {
      type: Array
    },
    invoiceProcessing: {
      type: Boolean
    }
  },
  data() {
    return {
      selectedTimelineFilter: '0',
      timelineFilters: [],
      currentTimelinePageNum: 1,
      collapsedIds: [],
      activeTabId: 'timeline-tab',
      timelineFilterList: [
        { label: 'Documents', id: 'documents' },
        { label: 'Due date', id: 'due date' },
        { label: 'External and Internal Messages', id: 'messages' },
        { label: 'Hours', id: 'hours' },
        { label: 'Invoices', id: 'invoices' },
        { label: 'Notes', id: 'notes' },
        { label: 'Retention', id: 'retention' },
        { label: 'Staff', id: 'staff' },
        { label: 'Status', id: 'status' },
        { label: 'Tags', id: 'tags' },
        { label: 'Tasks', id: 'tasks' },
        { label: 'Visibility', id: 'visibility' }
      ]
    }
  },
  watch: {
    timelineFilters() {
      this.currentTimelinePageNum = 1
      this.$emit('handle-timeline-filters', this.timelinePayload)
    },
    activeTabId(newValue) {
      this.$emit('tab-change', newValue)
    }
  },
  computed: {
    ...mapState(['account']),
    ...mapState('request_show', [
      'request',
      'currentAction',
      'totalRequestTimelineCount',
      'selectedTimelineEvent',
      'pinnedNote'
    ]),
    tasksPageNum: {
      set(tasksPageNum) {
        this.currentTasksPageNum = tasksPageNum
        this.$emit('handle-tasks-status-filters', this.tasksPayload())
      },
      get() {
        return this.currentTasksPageNum
      }
    },
    canShowMoreTimeline() {
      return this.timeline.length < this.totalRequestTimelineCount
    },
    editMessageVisibility() {
      return this.currentAction === 'editMessageVisibility'
    },
    editMessageText() {
      return this.currentAction === 'editMessageText'
    },
    timelinePageNum: {
      set(pageNum) {
        this.currentTimelinePageNum = pageNum
        this.$emit('handle-timeline-filters', this.timelinePayload)
      },
      get() {
        return this.currentTimelinePageNum
      }
    },
    timelinePayload() {
      return {
        filters: this.timelineFilters,
        page_number: this.currentTimelinePageNum
      }
    },
    ...mapState(['currentUser']),
    isCurrentRequester() {
      if (this.request.is_current_requester) {
        return true
      }

      return this.currentUser?.id === this.request?.requester?.id
    }
  },
  methods: {
    ...mapActions('request_show', [
      'filterByDocVisibility',
      'downloadInvoicePDF',
      'setCurrentAction',
      'setTimelinePin',
      'handlePinChange',
      'updateNotePin',
      'setActiveModal'
    ]),
    ...mapMutations('request_show', ['setSelectedTimelineEvent']),
    canPin(item) {
      return (
        this.account.pinning_notes_feature_flag &&
        item.timeline_name === 'Note' &&
        this.$ability.can('create', 'RequestEventPin')
      )
    },
    closeChangeMessageVisibilityModal() {
      this.setCurrentAction(null)
      this.setSelectedTimelineEvent(null)
    },
    handleChangeVisibility(item) {
      this.setSelectedTimelineEvent(item)
      this.setCurrentAction('editMessageVisibility')
    },
    handleEditMessageText(item) {
      this.setSelectedTimelineEvent(item)
      this.setCurrentAction('editMessageText')
    },
    handleCollapse(timelineId, itemCanCollapse) {
      if (!itemCanCollapse) return
      if (this.collapsedIds.includes(timelineId)) {
        this.collapsedIds = this.collapsedIds.filter((id) => id !== timelineId)
      } else {
        this.collapsedIds = [...this.collapsedIds, timelineId]
      }
    },
    canCollapse(typeCanCollapse, content) {
      return (
        typeCanCollapse || (!typeCanCollapse && (content || '').length >= 200)
      )
    },
    isCollapsed(timelineId) {
      return this.collapsedIds.includes(timelineId)
    },
    editIcon(item) {
      return ['External Message', 'Internal Message', 'Note'].includes(
        item.timeline_name
      )
    },
    changeVisibilityIcon(item) {
      return ['External Message', 'Internal Message'].includes(
        item.timeline_name
      )
    },
    timelineActions() {
      return this.$ability.can('read', 'publisher_only_items')
    },
    timelineItemDisplayText(timelineId, displayText) {
      if (!this.isCollapsed(timelineId)) return displayText
    },
    handleTimelineShowMore() {
      this.timelinePageNum += 1
    },
    timelineCardHeader(item) {
      if (item.timeline_name === 'External Message') {
        return item.timeline_guest_is_author
          ? 'Message from requester'
          : 'Message to requester'
      }
      return (
        item.timeline_name.charAt(0).toUpperCase() +
        item.timeline_name.slice(1).toLowerCase()
      )
    }
  }
}
</script>
<style lang="scss">
@import '@nextrequestco/components/src/styles/globals.scss';
@import '@nextrequestco/components/src/styles/tokens/variables.scss';

.invoice-timeline {
  margin-right: $spacing-16;
}

.nr-select-timeline {
  z-index: 1;
}

.rs-tabs.nr-tabs {
  .nr-select {
    width: 100%;
  }

  .nr-tabs-list {
    background-color: $white-ter;
    border-bottom: none;
  }

  .nr-tabs__tab {
    padding: 0;
  }

  .nr-tabs-button {
    position: relative;
    font-size: 1.5rem;
    color: $grey;
    background-color: unset;
    padding: #{calc(7 / 24)}em 1em;
    height: unset;
    border-color: transparent;
    border-radius: 0;

    &:hover {
      color: $grey;
    }

    &:focus {
      box-shadow: none;

      &::after {
        content: '';
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: dashed 2px $black;
      }
    }

    &--active {
      color: $black;
      font-weight: 600;
      border-width: 0;
      border-bottom: solid 2px $black;

      &::after {
        border-color: transparent;
      }

      &:hover {
        color: inherit;
      }
    }
  }
}
.invoice-links {
  width: 40%;
  border: 5px;
  display: flex;
  flex-direction: row;
  justify-content: right;
  align-items: flex-end;
  margin-left: auto;
  .nr-button {
    .nr-icon {
      margin-right: $spacing-6;
      margin-bottom: $spacing-4;
    }
  }
}
</style>
